import React, { useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Paper,
    Chip,
    Divider,
    Button,
    Table,
    TableBody,
    TableRow,
    TableCell,
    TextField,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Tabs,
    Tab,
} from "@mui/material";
import { Close as CloseIcon, LocalShipping as TruckIcon, Inventory as BoxIcon } from "@mui/icons-material";

const TornadoGPTOfflineFreightCalculationDisplay = ({
    tankFreightResult,
    boxFreightResult,
    onFreightPriceOverride,
}) => {
    const [showFreightDetails, setShowFreightDetails] = useState(false);
    const [overridePrice, setOverridePrice] = useState("");
    const [isOverridden, setIsOverridden] = useState(false);
    const [originalPrice, setOriginalPrice] = useState(null);
    const [selectedTab, setSelectedTab] = useState(0);

    if (!tankFreightResult || !boxFreightResult) {
        return (
            <Alert severity="warning">
                Freight calculation results are incomplete. Both tank and passthrough box calculations are
                required.
            </Alert>
        );
    }

    const totalFreightCost = tankFreightResult.totalFreightCost + boxFreightResult.totalFreightCost;
    const totalBaseFreightCost = tankFreightResult.baseFreightCost + boxFreightResult.baseFreightCost;

    const handleOverridePrice = () => {
        const newPrice = parseFloat(overridePrice);
        if (isNaN(newPrice) || newPrice < 0) {
            alert("Please enter a valid positive number");
            return;
        }

        if (!isOverridden) {
            setOriginalPrice(totalFreightCost);
        }

        setIsOverridden(true);
        if (onFreightPriceOverride) {
            onFreightPriceOverride(newPrice);
        }
    };

    const handleResetPrice = () => {
        setIsOverridden(false);
        setOverridePrice("");
        if (onFreightPriceOverride && originalPrice !== null) {
            onFreightPriceOverride(originalPrice);
        }
        setOriginalPrice(null);
    };

    const handleTabChange = (event, newValue) => {
        setSelectedTab(newValue);
    };

    const displayPrice = isOverridden ? parseFloat(overridePrice) : totalFreightCost;

    const renderFreightResultDetails = (result, title, icon) => {
        const { totalFreightCost, baseFreightCost, fuelSurcharge, consignmentCharge, breakdown } = result;
        const fuelSurchargeAmount = (baseFreightCost * fuelSurcharge) / 100;

        return (
            <Box>
                {/* Summary Cards */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={3}>
                        <Paper elevation={1} sx={{ p: 2, textAlign: "center" }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                                Shipping Type
                            </Typography>
                            <Typography variant="body2" fontWeight="bold" color="primary">
                                {breakdown.calculationType}
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Paper elevation={1} sx={{ p: 2, textAlign: "center" }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                                Load Type
                            </Typography>
                            <Typography variant="body2" fontWeight="bold" color="success.main">
                                {breakdown.loadType}
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Paper elevation={1} sx={{ p: 2, textAlign: "center" }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                                Distance
                            </Typography>
                            <Typography variant="body2" fontWeight="bold" color="secondary">
                                {breakdown.distance.toFixed(1)} km
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Paper elevation={1} sx={{ p: 2, textAlign: "center" }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                                Rate
                            </Typography>
                            <Typography variant="body2" fontWeight="bold" color="warning.main">
                                ${breakdown.rate.toFixed(2)}/km
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>

                {/* Main Content Grid */}
                <Grid container spacing={3}>
                    <Grid item xs={12} lg={6}>
                        {/* Shipment Dimensions */}
                        <Card variant="outlined" sx={{ mb: 2 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Shipment Dimensions
                                </Typography>
                                <Chip
                                    label={breakdown.shipment.calculationPath}
                                    color="primary"
                                    variant="outlined"
                                    sx={{ mb: 2 }}
                                />
                                <Grid container spacing={2}>
                                    <Grid item xs={4}>
                                        <Paper sx={{ p: 1, textAlign: "center", bgcolor: "grey.50" }}>
                                            <Typography variant="caption" color="text.secondary">
                                                Width
                                            </Typography>
                                            <Typography variant="body2" fontWeight="bold">
                                                {breakdown.shipment.width.toFixed(2)}m
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Paper sx={{ p: 1, textAlign: "center", bgcolor: "grey.50" }}>
                                            <Typography variant="caption" color="text.secondary">
                                                Height
                                            </Typography>
                                            <Typography variant="body2" fontWeight="bold">
                                                {breakdown.shipment.height.toFixed(2)}m
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Paper sx={{ p: 1, textAlign: "center", bgcolor: "grey.50" }}>
                                            <Typography variant="caption" color="text.secondary">
                                                Length
                                            </Typography>
                                            <Typography variant="body2" fontWeight="bold">
                                                {breakdown.shipment.length.toFixed(2)}m
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>

                        {/* Transport Information */}
                        <Card variant="outlined" sx={{ mb: 2 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Transport Information
                                </Typography>
                                <Table size="small">
                                    <TableBody>
                                        <TableRow>
                                            <TableCell>Destination Zone:</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: "bold" }}>
                                                {breakdown.transportRate.zoneName}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Postcode:</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: "bold" }}>
                                                {breakdown.transportRate.postcode}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Distance:</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: "bold" }}>
                                                {breakdown.distance.toFixed(2)} km
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} lg={6}>
                        {/* Calculation Logic */}
                        <Card variant="outlined" sx={{ mb: 2 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Calculation Logic
                                </Typography>
                                <Paper sx={{ p: 2, mb: 2, bgcolor: "warning.50" }}>
                                    <Typography
                                        variant="body2"
                                        fontWeight="bold"
                                        color="warning.dark"
                                        gutterBottom
                                    >
                                        Why this rate was selected:
                                    </Typography>
                                    <Typography variant="body2" color="warning.dark">
                                        {breakdown.explanation}
                                    </Typography>
                                </Paper>
                                <Paper sx={{ p: 2, bgcolor: "info.50" }}>
                                    <Typography
                                        variant="body2"
                                        fontWeight="bold"
                                        color="info.dark"
                                        gutterBottom
                                    >
                                        Calculation Formula:
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ fontFamily: "monospace", mb: 1 }}
                                        color="info.dark"
                                    >
                                        {breakdown.formula}
                                    </Typography>
                                    <Typography variant="body2" fontWeight="bold" color="info.dark">
                                        = ${baseFreightCost.toFixed(2)}
                                    </Typography>
                                </Paper>
                            </CardContent>
                        </Card>

                        {/* Cost Breakdown */}
                        <Card variant="outlined" sx={{ mb: 2 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Cost Breakdown
                                </Typography>
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        p: 2,
                                        mb: 1,
                                        bgcolor: "grey.50",
                                        borderRadius: 1,
                                    }}
                                >
                                    <Typography>Base Freight Cost:</Typography>
                                    <Typography variant="h6" fontWeight="bold">
                                        ${baseFreightCost.toFixed(2)}
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        p: 2,
                                        mb: 1,
                                        bgcolor: "warning.50",
                                        borderRadius: 1,
                                    }}
                                >
                                    <Typography>Fuel Surcharge ({fuelSurcharge.toFixed(1)}%):</Typography>
                                    <Typography fontWeight="bold" color="warning.dark">
                                        + ${fuelSurchargeAmount.toFixed(2)}
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        p: 2,
                                        mb: 2,
                                        bgcolor: "info.50",
                                        borderRadius: 1,
                                    }}
                                >
                                    <Typography>Consignment Charge:</Typography>
                                    <Typography fontWeight="bold" color="info.dark">
                                        + ${consignmentCharge.toFixed(2)}
                                    </Typography>
                                </Box>
                                <Divider sx={{ my: 2 }} />
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        p: 3,
                                        bgcolor: "success.100",
                                        borderRadius: 1,
                                    }}
                                >
                                    <Typography variant="h6" fontWeight="bold">
                                        {title} Total:
                                    </Typography>
                                    <Typography variant="h5" fontWeight="bold" color="success.dark">
                                        ${totalFreightCost.toFixed(2)}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        );
    };

    return (
        <Box>
            <Button variant="outlined" onClick={() => setShowFreightDetails(true)} sx={{ mb: 2 }}>
                Show Freight Price Info
            </Button>

            <Dialog
                open={showFreightDetails}
                onClose={() => setShowFreightDetails(false)}
                maxWidth="xl"
                fullWidth
                scroll="paper"
            >
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="h5" color="primary">
                        TornadoGPT Offline Freight Calculation
                    </Typography>
                    <IconButton
                        onClick={() => setShowFreightDetails(false)}
                        sx={{ color: (theme) => theme.palette.grey[500] }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers>
                    {/* Combined Total Price Display */}
                    <Box sx={{ textAlign: "center", mb: 3 }}>
                        <Typography
                            variant="h4"
                            color={isOverridden ? "warning.main" : "success.main"}
                            gutterBottom
                        >
                            ${displayPrice.toFixed(2)}
                            {isOverridden && (
                                <Chip label="MANUAL OVERRIDE" color="warning" size="small" sx={{ ml: 2 }} />
                            )}
                        </Typography>
                        <Typography variant="subtitle1" color="text.secondary">
                            {isOverridden ? "Overridden" : "Total Calculated"} Freight Cost (Both Components)
                        </Typography>

                        {/* Individual Component Costs */}
                        <Grid container spacing={2} sx={{ mt: 2, justifyContent: "center" }}>
                            <Grid item>
                                <Paper
                                    elevation={2}
                                    sx={{ p: 2, textAlign: "center", bgcolor: "primary.50" }}
                                >
                                    <TruckIcon color="primary" sx={{ mb: 1 }} />
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        Tank Freight
                                    </Typography>
                                    <Typography variant="h6" fontWeight="bold" color="primary">
                                        ${tankFreightResult.totalFreightCost.toFixed(2)}
                                    </Typography>
                                </Paper>
                            </Grid>
                            <Grid item>
                                <Paper
                                    elevation={2}
                                    sx={{ p: 2, textAlign: "center", bgcolor: "secondary.50" }}
                                >
                                    <BoxIcon color="secondary" sx={{ mb: 1 }} />
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        Passthrough Box
                                    </Typography>
                                    <Typography variant="h6" fontWeight="bold" color="secondary">
                                        ${boxFreightResult.totalFreightCost.toFixed(2)}
                                    </Typography>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Price Override Section */}
                    <Card variant="outlined" sx={{ mb: 3, bgcolor: "grey.50" }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Freight Price Override
                            </Typography>
                            {isOverridden && (
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    Freight price has been manually overridden. Original calculated price: $
                                    {originalPrice?.toFixed(2)}
                                </Alert>
                            )}
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="Override Total Freight Price"
                                        type="number"
                                        value={overridePrice}
                                        onChange={(e) => setOverridePrice(e.target.value)}
                                        placeholder={totalFreightCost.toFixed(2)}
                                        InputProps={{
                                            startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                                            inputProps: { min: 0, step: 0.01 },
                                        }}
                                        helperText="Enter new total freight price to override calculation"
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Button
                                        variant="contained"
                                        onClick={handleOverridePrice}
                                        disabled={!overridePrice}
                                        sx={{ mr: 1 }}
                                    >
                                        Override Price
                                    </Button>
                                    {isOverridden && (
                                        <Button
                                            variant="outlined"
                                            onClick={handleResetPrice}
                                            color="secondary"
                                        >
                                            Reset to Calculated
                                        </Button>
                                    )}
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Typography variant="body2" color="text.secondary">
                                        Current Price: <strong>${displayPrice.toFixed(2)}</strong>
                                        {isOverridden && (
                                            <Chip
                                                label="OVERRIDDEN"
                                                color="warning"
                                                size="small"
                                                sx={{ ml: 1 }}
                                            />
                                        )}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Detailed Breakdown - Only show if not overridden */}
                    {!isOverridden && (
                        <>
                            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
                                <Tabs value={selectedTab} onChange={handleTabChange} centered>
                                    <Tab
                                        label="Tank Freight Details"
                                        icon={<TruckIcon />}
                                        iconPosition="start"
                                    />
                                    <Tab
                                        label="Passthrough Box Details"
                                        icon={<BoxIcon />}
                                        iconPosition="start"
                                    />
                                    <Tab label="Combined Summary" />
                                </Tabs>
                            </Box>

                            {selectedTab === 0 &&
                                renderFreightResultDetails(tankFreightResult, "Tank", <TruckIcon />)}
                            {selectedTab === 1 &&
                                renderFreightResultDetails(boxFreightResult, "Passthrough Box", <BoxIcon />)}

                            {selectedTab === 2 && (
                                <Box>
                                    <Typography variant="h6" gutterBottom>
                                        Combined Freight Summary
                                    </Typography>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={6}>
                                            <Card variant="outlined">
                                                <CardContent>
                                                    <Typography variant="h6" color="primary" gutterBottom>
                                                        <TruckIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                                                        Tank Component
                                                    </Typography>
                                                    <Table size="small">
                                                        <TableBody>
                                                            <TableRow>
                                                                <TableCell>Base Cost:</TableCell>
                                                                <TableCell align="right">
                                                                    $
                                                                    {tankFreightResult.baseFreightCost.toFixed(
                                                                        2
                                                                    )}
                                                                </TableCell>
                                                            </TableRow>
                                                            <TableRow>
                                                                <TableCell>Fuel Surcharge:</TableCell>
                                                                <TableCell align="right">
                                                                    $
                                                                    {(
                                                                        (tankFreightResult.baseFreightCost *
                                                                            tankFreightResult.fuelSurcharge) /
                                                                        100
                                                                    ).toFixed(2)}
                                                                </TableCell>
                                                            </TableRow>
                                                            <TableRow>
                                                                <TableCell>Consignment:</TableCell>
                                                                <TableCell align="right">
                                                                    $
                                                                    {tankFreightResult.consignmentCharge.toFixed(
                                                                        2
                                                                    )}
                                                                </TableCell>
                                                            </TableRow>
                                                            <TableRow>
                                                                <TableCell>
                                                                    <strong>Subtotal:</strong>
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    <strong>
                                                                        $
                                                                        {tankFreightResult.totalFreightCost.toFixed(
                                                                            2
                                                                        )}
                                                                    </strong>
                                                                </TableCell>
                                                            </TableRow>
                                                        </TableBody>
                                                    </Table>
                                                </CardContent>
                                            </Card>
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <Card variant="outlined">
                                                <CardContent>
                                                    <Typography variant="h6" color="secondary" gutterBottom>
                                                        <BoxIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                                                        Passthrough Box
                                                    </Typography>
                                                    <Table size="small">
                                                        <TableBody>
                                                            <TableRow>
                                                                <TableCell>Base Cost:</TableCell>
                                                                <TableCell align="right">
                                                                    $
                                                                    {boxFreightResult.baseFreightCost.toFixed(
                                                                        2
                                                                    )}
                                                                </TableCell>
                                                            </TableRow>
                                                            <TableRow>
                                                                <TableCell>Fuel Surcharge:</TableCell>
                                                                <TableCell align="right">
                                                                    $
                                                                    {(
                                                                        (boxFreightResult.baseFreightCost *
                                                                            boxFreightResult.fuelSurcharge) /
                                                                        100
                                                                    ).toFixed(2)}
                                                                </TableCell>
                                                            </TableRow>
                                                            <TableRow>
                                                                <TableCell>Consignment:</TableCell>
                                                                <TableCell align="right">
                                                                    $
                                                                    {boxFreightResult.consignmentCharge.toFixed(
                                                                        2
                                                                    )}
                                                                </TableCell>
                                                            </TableRow>
                                                            <TableRow>
                                                                <TableCell>
                                                                    <strong>Subtotal:</strong>
                                                                </TableCell>
                                                                <TableCell align="right">
                                                                    <strong>
                                                                        $
                                                                        {boxFreightResult.totalFreightCost.toFixed(
                                                                            2
                                                                        )}
                                                                    </strong>
                                                                </TableCell>
                                                            </TableRow>
                                                        </TableBody>
                                                    </Table>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    </Grid>

                                    <Box sx={{ mt: 3, textAlign: "center" }}>
                                        <Paper elevation={3} sx={{ p: 3, bgcolor: "success.100" }}>
                                            <Typography variant="h5" fontWeight="bold" color="success.dark">
                                                Grand Total: ${totalFreightCost.toFixed(2)}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Combined freight cost for both components
                                            </Typography>
                                        </Paper>
                                    </Box>
                                </Box>
                            )}
                        </>
                    )}

                    {isOverridden && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            The total freight price has been manually overridden to $
                            {parseFloat(overridePrice).toFixed(2)}. Use the tabs above to view the original
                            calculated breakdown for each component.
                        </Alert>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setShowFreightDetails(false)} variant="contained">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TornadoGPTOfflineFreightCalculationDisplay;
